import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
    Box,
    Heading,
    FormControl,
    FormLabel,
    Input,
    Button,
    Alert,
    AlertIcon,
    Container,
    VStack,
    Text
} from '@chakra-ui/react';

const ResetPasswordPage = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [validToken, setValidToken] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { resetPassword } = useContext(AuthContext);

    // Get key and uidb36 from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('key');
    const uidb36 = searchParams.get('uidb36');
    const errorParam = searchParams.get('error');

    useEffect(() => {
        // Check if we have an error parameter
        if (errorParam) {
            switch (errorParam) {
                case 'invalid_key':
                    setError('Invalid password reset link. The link may have expired.');
                    break;
                case 'invalid_token':
                    setError('Invalid password reset token. The link may have expired.');
                    break;
                case 'invalid_user':
                    setError('Invalid user. The link may have expired or been used already.');
                    break;
                case 'missing_parameters':
                    setError('Missing required parameters. Please request a new password reset link.');
                    break;
                default:
                    setError('An error occurred. Please request a new password reset link.');
            }
            setValidToken(false);
            return;
        }

        // Check if we have the required parameters
        if (!key || !uidb36) {
            setError('Invalid password reset link. Please request a new one.');
            setValidToken(false);
        } else {
            setValidToken(true);
        }
    }, [key, uidb36, errorParam]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            setLoading(false);
            return;
        }

        try {
            await resetPassword(uidb36, key, password, confirmPassword);
            navigate('/?reset=success');
        } catch (err) {
            // AuthContext.resetPassword sets the error state and throws the
            // backend error object (or a generic Error). Extract details if available.
            if (err.new_password1) {
                setError(err.new_password1[0]);
            } else if (err.new_password2) {
                setError(err.new_password2[0]);
            } else if (err.non_field_errors) {
                setError(err.non_field_errors[0]);
            } else if (err.token) {
                setError('Reset link has expired. Please request a new one.');
            } else if (!error) {
                setError('Failed to reset password. Please try again.');
            }
            setLoading(false);
        }
    };

    if (!validToken) {
        return (
            <Container maxW="md" centerContent py={20}>
                <Box
                    w="100%"
                    p={8}
                    borderRadius="2xl"
                    bg="whiteAlpha.50"
                    backdropFilter="blur(10px)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
                >
                    <VStack spacing={6} align="stretch">
                        <Heading
                            size="xl"
                            bgGradient="linear(to-r, #667eea, #764ba2)"
                            bgClip="text"
                            textAlign="center"
                        >
                            Password Reset
                        </Heading>
                        <Alert status="error" borderRadius="lg">
                            <AlertIcon />
                            {error}
                        </Alert>
                        <Button
                            onClick={() => navigate('/')}
                            size="lg"
                            bgGradient="linear(to-r, #667eea, #764ba2)"
                            _hover={{
                                bgGradient: "linear(to-r, #764ba2, #667eea)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.6)"
                            }}
                            color="white"
                        >
                            Return to Home
                        </Button>
                    </VStack>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxW="md" centerContent py={20}>
            <Box
                w="100%"
                p={8}
                borderRadius="2xl"
                bg="whiteAlpha.50"
                backdropFilter="blur(10px)"
                border="1px solid"
                borderColor="whiteAlpha.200"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
            >
                <VStack spacing={6} align="stretch">
                    <Heading
                        size="xl"
                        bgGradient="linear(to-r, #667eea, #764ba2)"
                        bgClip="text"
                        textAlign="center"
                    >
                        Reset Your Password
                    </Heading>

                    {error && (
                        <Alert status="error" borderRadius="lg">
                            <AlertIcon />
                            {error}
                        </Alert>
                    )}

                    <Box as="form" onSubmit={handleSubmit}>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel color="whiteAlpha.900" fontWeight="medium">
                                    New Password
                                </FormLabel>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    isDisabled={loading}
                                    size="lg"
                                    color="white"
                                    bg="whiteAlpha.100"
                                    borderColor="purple.500"
                                    _placeholder={{ color: "whiteAlpha.500" }}
                                    _hover={{ bg: "whiteAlpha.150", borderColor: "purple.400" }}
                                    _focus={{ bg: "whiteAlpha.150", borderColor: "purple.300", boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.6)" }}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel color="whiteAlpha.900" fontWeight="medium">
                                    Confirm New Password
                                </FormLabel>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    isDisabled={loading}
                                    size="lg"
                                    color="white"
                                    bg="whiteAlpha.100"
                                    borderColor="purple.500"
                                    _placeholder={{ color: "whiteAlpha.500" }}
                                    _hover={{ bg: "whiteAlpha.150", borderColor: "purple.400" }}
                                    _focus={{ bg: "whiteAlpha.150", borderColor: "purple.300", boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.6)" }}
                                />
                            </FormControl>

                            <Button
                                type="submit"
                                isLoading={loading}
                                loadingText="Resetting Password"
                                size="lg"
                                w="100%"
                                mt={4}
                                bgGradient="linear(to-r, #667eea, #764ba2)"
                                _hover={{
                                    bgGradient: "linear(to-r, #764ba2, #667eea)",
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.6)"
                                }}
                                color="white"
                                fontWeight="bold"
                            >
                                Reset Password
                            </Button>
                        </VStack>
                    </Box>
                </VStack>
            </Box>
        </Container>
    );
};

export default ResetPasswordPage; 